import React, { useCallback, useState } from 'react';
import styled from 'styled-components';

import { TooltipCreator } from '../generators/base';
import { useChartEvent } from '../hooks';

/**
 * Derive annotation width based on item width,
 * making it discrete so that it doesn't change very often,
 * which results in less ECharts option updates.
 * if markPoint is smaller than 6, it is basically invisible,
 * so the minimal size is 6
 * @param itemWidth
 */
export const getMarkPointWidth = (itemWidth: number): number => {
  // set the max item width to 20, mark being too big doesn't look good
  for (const threshold of [20, 12, 8]) {
    if (itemWidth > threshold) {
      return Math.round(threshold * 0.75);
    }
  }
  return 6;
};

const MarkTooltipStyles = styled.div`
  position: fixed;
  z-index: 8; /* react-stockchart has z-index 6 and char buttons has z-index 7*/
  width: 200px;
  transform: translate(-50%, -110%);
  background-color: white;
  border-radius: 5px;
  padding: 5px;
  box-shadow: 0px 0px 5px #888;
  font-size: 0.8rem;
`;

/**
 * We have multiple mark point generators,
 * and we only want to call a specific generator's
 * tooltip creator, so here we embed the generator
 * index into data's name.
 */
export const embedIndexIntoMarkPointName = (
  name: string,
  index: number
): string => `${index}||${name}`;

/**
 * the opposite of embedIndexIntoMarkPointName
 */
const resolveMarkPointName = (
  markPointName: string
): {
  name: string;
  index: number;
} => {
  const [indexString, name] = markPointName.split('||');
  return { name, index: Number.parseInt(indexString) };
};

/**
 * Setup the mouse over listener and return
 * the tooltip element generated by the corresponding
 * mark generator's tooltip creator.
 */
export const useMarkTooltip = (
  chart: echarts.ECharts | undefined,
  markTooltipCreaters: TooltipCreator[]
): React.ReactElement => {
  const [markTooltip, setMarkTooltip] = useState<{
    clientX: number;
    clientY: number;
    element: React.ReactElement;
  } | null>();

  const handleMouseOverMark = useCallback(
    params => {
      const { componentType } = params;
      if (componentType === 'markPoint') {
        const {
          data: { name: embeddedName, coord },
          event: {
            event: { clientX, clientY },
          },
        } = params;
        const { name, index } = resolveMarkPointName(embeddedName);
        const element = markTooltipCreaters[index](name, coord);
        setMarkTooltip({
          element,
          clientX,
          clientY,
        });
      }
    },
    [markTooltipCreaters]
  );

  const handleMouseOutMark = useCallback(params => {
    const { componentType } = params;
    if (componentType === 'markPoint') {
      setMarkTooltip(null);
    }
  }, []);

  useChartEvent(chart, 'mouseover', handleMouseOverMark);
  useChartEvent(chart, 'mouseout', handleMouseOutMark);

  return (
    markTooltip && (
      <MarkTooltipStyles
        style={{
          left: `${markTooltip.clientX}px`,
          top: `${markTooltip.clientY}px`,
        }}
      >
        {markTooltip.element}
      </MarkTooltipStyles>
    )
  );
};
